import axios, { AxiosInstance } from 'axios';

let API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

API_URL = String(API_URL || '').trim();
API_URL = API_URL.replace(/\/+$/, '');

if (process.env.NODE_ENV === 'development') {
  if (API_URL === '/api' || API_URL.startsWith('/api/')) {
    API_URL = `http://localhost:5000${API_URL}`;
  } else {
    try {
      const u = new URL(API_URL);
      if (u.pathname === '' || u.pathname === '/') {
        API_URL = `${API_URL}/api`;
      }
    } catch (_) {
    }
  }
}

// Debug: log which API URL the frontend build is using
// This helps verify that Vercel production has REACT_APP_API_URL set correctly
console.log('SuperGauge API_URL (build-time):', API_URL);

const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add a request interceptor to add the auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['x-auth-token'] = token;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle common errors
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      // Handle unauthorized errors (e.g., token expired)
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post<{ token: string }>('/auth/login', { email, password }),
  register: (userData: {
    firstName?: string;
    lastName?: string;
    name?: string;
    email: string;
    password: string;
    role?: string;
    department?: string;
    position?: string;
    organizationName?: string;
  }) => api.post<{ token: string }>('/auth/register', userData),
  setPassword: (token: string, password: string) =>
    api.post<{ message: string }>('/auth/set-password', { token, password }),
  getMe: () => api.get('/auth/me'),
};

// Survey Assignment API
export const assignmentAPI = {
  create: (body: {
    survey: string;
    team?: string;
    users?: string[];
    startDate?: string;
    endDate?: string;
    isActive?: boolean;
    anonymousResponses?: boolean;
    note?: string;
  }) => api.post('/assignments', body),
  list: (surveyId: string) => api.get(`/assignments`, { params: { survey: surveyId } }),
  update: (id: string, body: Partial<{ startDate: string; endDate: string; isActive: boolean; anonymousResponses: boolean; note: string }>) =>
    api.put(`/assignments/${id}`, body),
  delete: (id: string) => api.delete(`/assignments/${id}`),
};

// Survey API
export const surveyAPI = {
  getSurveys: () => api.get('/surveys'),
  getSurvey: (id: string) => api.get(`/surveys/${id}`),
  endSurvey: (id: string) => api.post(`/surveys/${id}/end`),
  cancelSurvey: (id: string) => api.post(`/surveys/${id}/cancel`),
  archiveSurvey: (id: string) => api.post(`/surveys/${id}/archive`),
  unarchiveSurvey: (id: string) => api.post(`/surveys/${id}/unarchive`),
  useTemplate: (id: string, body: Partial<{ startDate: string; endDate: string; targetDepartment: string; anonymousResponses: boolean }>) =>
    api.post(`/surveys/templates/${id}/use`, body),
  createSurvey: (surveyData: any) => api.post('/surveys', surveyData),
  updateSurvey: (id: string, surveyData: any) =>
    api.put(`/surveys/${id}`, surveyData),
  deleteSurvey: (id: string) => api.delete(`/surveys/${id}`),
  submitResponse: (surveyId: string, responseData: any) =>
    api.post(`/surveys/${surveyId}/responses`, responseData),
  getSurveyResponses: (surveyId: string) =>
    api.get(`/surveys/${surveyId}/responses`),
  // Templates
  getTemplates: () => api.get('/surveys/templates'),
  createFromTemplate: (templateId: string, body: any) =>
    api.post(`/surveys/templates/${templateId}/use`, body),
};

// Response API
export const responseAPI = {
  getMyResponses: () => api.get('/responses/me'),
  getMyResponse: (surveyId: string) =>
    api.get(`/responses/me/${surveyId}`),
};

// User API (Admin only)
export const userAPI = {
  getUsers: () => api.get('/users'),
  getUser: (id: string) => api.get(`/users/${id}`),
  createUser: (userData: {
    firstName?: string;
    lastName?: string;
    name?: string;
    email: string;
    password: string;
    role: string;
    department?: string;
    position?: string;
    organization?: string;
    team?: string;
    accountType?: 'basic' | 'standard';
  }) => api.post('/users', userData),
  updateUser: (id: string, userData: any) => api.put(`/users/${id}`, userData),
  deleteUser: (id: string) => api.delete(`/users/${id}`),
  resendInvite: (id: string) => api.post(`/users/${id}/resend-invite`, {}),
};

// Organization API (Admin only)
export const organizationAPI = {
  getOrganizations: () => api.get('/organizations'),
  getOrganization: (id: string) => api.get(`/organizations/${id}`),
  createOrganization: (orgData: {
    name: string;
    description?: string;
    domain?: string;
    industry?: string;
    size?: string;
    settings?: any;
  }) => api.post('/organizations', orgData),
  updateOrganization: (id: string, orgData: any) => api.put(`/organizations/${id}`, orgData),
  deleteOrganization: (id: string) => api.delete(`/organizations/${id}`),
  getOrganizationUsers: (id: string) => api.get(`/organizations/${id}/users`),
};

// Team API (Manager/Admin)
export const teamAPI = {
  getTeams: () => api.get('/teams'),
  getTeam: (id: string) => api.get(`/teams/${id}`),
  createTeam: (teamData: {
    name: string;
    description?: string;
    department?: string;
    members?: string[];
  }) => api.post('/teams', teamData),
  updateTeam: (id: string, teamData: any) => api.put(`/teams/${id}`, teamData),
  deleteTeam: (id: string) => api.delete(`/teams/${id}`),
  addMember: (id: string, userId: string) => api.post(`/teams/${id}/members`, { userId }),
  removeMember: (id: string, userId: string) => api.delete(`/teams/${id}/members/${userId}`),
};

// Recurring Assignments API (Manager/Admin)
export const recurringAPI = {
  create: (body: {
    userId: string;
    surveyTemplateId: string;
    intervalWeeks: number;
    weekday: number;
    timeOfDay: string; // 'HH:mm'
    timezone: string;
    startDate: string; // ISO date
    endDate?: string | null;
  }) => api.post('/recurring', body),
  list: () => api.get('/recurring'),
  update: (id: string, body: Partial<{ intervalWeeks: number; weekday: number; timeOfDay: string; timezone: string; startDate: string; endDate: string | null; status: 'active' | 'paused' | 'cancelled' }>) =>
    api.put(`/recurring/${id}`, body),
  delete: (id: string) => api.delete(`/recurring/${id}`),
  runDue: () => api.post('/recurring/run-due', {}),
  runNow: (id: string) => api.post(`/recurring/${id}/run`, {}),
};

// Resources API
export const resourcesAPI = {
  getAll: () => api.get('/resources'),
  getById: (id: string) => api.get(`/resources/${id}`),
  getByCategory: (category: string) => api.get(`/resources/category/${category}`),
};

// Community Directory API
export const communityAPI = {
  listPublic: (params?: { q?: string; type?: 'company' | 'individual'; service?: string; featured?: boolean }) =>
    api.get('/community', { params }),
  getPublicById: (id: string) => api.get(`/community/${id}`),
  submit: (body: any) => api.post('/community/submit', body),
  getMe: () => api.get('/community/me'),
  updateMe: (body: any) => api.put('/community/me', body),
  adminList: () => api.get('/community/admin'),
  adminCreate: (body: any) => api.post('/community/admin', body),
  adminUpdate: (id: string, body: any) => api.put(`/community/admin/${id}`, body),
  adminDelete: (id: string) => api.delete(`/community/admin/${id}`),
};

export default api;
